import React, { useEffect, useRef, useState } from "react";
import { ButtonGroup, Card } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetHePlanQuery } from "../../app/features/apiSlice";
import Loader from "../Loader";
import Error from "../Error";
import { formatNumberToLocal } from "../../helpers";
import CurrencyValue from "../CurrencyValue";
import { Column } from "primereact/column";
import moment from "moment";
import { apiSlice } from "../../app/features/apiSlice";
import ExportCSVButton from "../exportcsv/ExportCsvButton";
import SearchBox from "../SearchBox";
import ReusableDataTable from "../ReusableDataTable";

const initialSorting = {
  sortBy: "id",
  sortOrder: -1,
};

const HeliorPlanList = ({ pageTitle }) => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const sessionId = useRef(Date.now()).current;
  const [tableData, setTableData] = useState([]);
  const [isLoading2, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sorting, setSorting] = useState(initialSorting);
  const { data, isLoading, isSuccess, isError, error, isFetching } =
    useGetHePlanQuery({
      ...sorting,
      sessionId,
      searchQuery: searchQuery,
      page: currentPage,
      limit: 10,
    });

  const processItemForExport = (item) => {
    return {
      [t("contract_date")]: moment(item?.contract_date).format("DD-MM-YYYY"),
      [t("contract_code")]: item?.contract_code,
      [t("collaborator")]:
        `${item?.client?.orgUser?.referrer?.user?.first_name} ${item?.client?.orgUser?.referrer?.user?.last_name}`,
      [t("client")]: `${item?.client?.first_name} ${item?.client?.last_name}`,
      [t("number_of_recurring_payments")]: item?.number_of_recurring_payments,
      [t("payment_amount")]: formatNumberToLocal(item?.payment_amount || 0),
      [t("recurring_amount_paid")]: formatNumberToLocal(
        item?.recurring_amount_paid || 0,
      ),
      [t("doc_share")]: formatNumberToLocal(item?.doc_share || 0),
      [t("unpaid_installments")]: item?.unpaid_installments,
    };
  };

  useEffect(() => {
    if (isSuccess) {
      setIsLoading(true);
      const items = data?.data;
      const tableItems = [];
      items.forEach((item) => {
        let clientName = "";
        if (item?.client?.orgUser?.type === "COMPANY") {
          clientName = item?.client?.orgUser.company_name;
        } else {
          clientName = `${item?.client?.first_name} ${item?.client?.last_name}`;
        }

        let collaboratorName = "-";

        if (item?.client?.orgUser?.referrer?.type === "COMPANY") {
          collaboratorName =
            item?.client?.orgUser?.referrer?.company_name || "-";
        } else if (
          item?.client?.orgUser?.referrer?.user?.first_name &&
          item?.client?.orgUser?.referrer?.user?.last_name
        ) {
          collaboratorName =
            `${item?.client?.orgUser?.referrer?.user.first_name} ${item?.client?.orgUser?.referrer?.user.last_name}` ||
            "-";
        } else {
          collaboratorName = "-";
        }

        tableItems.push({
          id: item.id,
          status: item.status,
          contract_date: moment(item?.contract_date).format("DD-MM-YYYY"),
          contract_code: item?.contract_code,
          client: clientName ? clientName.trim() : "-",
          clientId: item?.client?.id,
          collaborator: collaboratorName,
          collaboratorId: item?.client?.orgUser?.referrer?.user?.id,
          number_of_recurring_payments: item?.number_of_recurring_payments,
          payment_amount: item?.payment_amount ?? 0,
          recurring_amount_paid: item?.recurring_amount_paid ?? 0,
          doc_share: item?.doc_share ?? 0,
          unpaid_installments: item?.unpaid_installments,
          currency: item?.currency,
          fx_rate: item?.fx_rate,
          action: "",
        });
      });
      setTableData(tableItems);
      setIsLoading(false);
    }
  }, [isSuccess, data]);

  const totalRecords = data?.pagination?.total || 0;
  if (isLoading) return <Loader />;
  if (isError) return <Error error={error} />;

  return (
    <>
      <Card bg="dark1 text-light">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>{pageTitle} List</div>
          <div className="d-flex justify-content-between align-items-center">
            <ExportCSVButton
              apiFn={apiSlice.endpoints.getHePlan.initiate}
              processItem={processItemForExport}
              filenamePrefix="export_hplan_list"
              queryParams={{
                sessionId,
                searchQuery,
                ...sorting,
              }}
            />
            <SearchBox
              setCurrentPage={setCurrentPage}
              setSearchQuery={setSearchQuery}
            />
          </div>
        </Card.Header>
        <Card.Body>
          {(isLoading || isLoading2) && <Loader />}
          {isError && <Error error={error} />}
          {isSuccess && (
            <ReusableDataTable
              tableData={tableData}
              currentPage={currentPage}
              totalRecords={totalRecords}
              isLoading={isLoading}
              isFetching={isFetching}
              sorting={sorting}
              searchQuery={searchQuery}
              setSorting={setSorting}
              setCurrentPage={setCurrentPage}
              initialSorting={initialSorting}
            >
              <Column
                sortable
                field="contract_date"
                header={t("contract_date")}
              />
              <Column
                sortable
                field="contract_code"
                header={t("contract_code")}
              />
              <Column
                sortable
                field="collaborator"
                header={t("collaborator")}
                body={(rowData) => (
                  <Link
                    className="text-light"
                    to={`/${lang}/collaborator/${rowData.collaboratorId}`}
                  >
                    {rowData.collaborator}
                  </Link>
                )}
              />
              <Column
                sortable
                field="client"
                header={t("client")}
                body={(rowData) => (
                  <Link
                    className="text-light"
                    to={`/${lang}/client/${rowData.clientId}`}
                  >
                    {rowData.client}
                  </Link>
                )}
              />
              <Column
                sortable
                field="number_of_recurring_payments"
                header={t("noOfPayments")}
              />
              <Column
                sortable
                field="payment_amount"
                header={t("payment_amount")}
                body={(rowData) => (
                  <CurrencyValue amount={rowData.payment_amount} currency={rowData.currency} rate={rowData.fx_rate} />
                )}
              />
              <Column
                sortable
                field="recurring_amount_paid"
                header={t("recurring_amount_paid")}
                body={(rowData) => (
                  <CurrencyValue amount={rowData.recurring_amount_paid} currency={rowData.currency} rate={rowData.fx_rate} />
                )}
              />
              <Column
                sortable
                field="doc_share"
                header={t("doc_share")}
                body={(rowData) => (
                  <CurrencyValue amount={rowData.doc_share} currency={rowData.currency} rate={rowData.fx_rate} />
                )}
              />
              <Column
                sortable
                field="unpaid_installments"
                header={t("unpaid_installments")}
              />
              <Column
                body={(rowData) => (
                  <ButtonGroup size="sm">
                    <Link
                      to={`/${lang}/collaborator/excelior-plan/${rowData.contract_code}`}
                      className="btn btn-outline-light"
                    >
                      <i className="fa fa-eye" />
                    </Link>
                  </ButtonGroup>
                )}
              />
            </ReusableDataTable>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default HeliorPlanList;
